import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { setNotificationsAccountTab } from "@redux/slices/notificationsV2AccountSlice";
import {
  GroupsContentCard,
  GroupsKpisSection,
  GroupsTabProvider,
  useGroupsTabContext,
} from "./Groups/GroupsTab";
import MyNotificationsTab from "./MyNotifications/MyNotificationsTab";

/**
 * Account → Notifications V2 shell (`/account/notifications`).
 * The Notification Groups list and My Notifications tab mount here; group
 * create/edit (including linked-tag hydration from `GET /groups/:id`
 * `tagResources` + `linkedTagId`) lives in `GroupForm/GroupFormPage.tsx`.
 *
 * Linked-tag dropdown options are filtered via Tags Manager §3.0 / §3.9
 * (`filterTagsForNotificationLink` in `TagsManager/tagLinkSelectOptions.ts`).
 */
const TAB_OPTIONS = [
  { label: "Notification Groups", value: "groups" },
  { label: "My Notifications", value: "me" },
] as const;

/**
 * Inner shell rendered inside `GroupsTabProvider`.
 *
 * The tabs strip stays mounted for every state (loading, populated,
 * filter-empty and true-empty) so `My Notifications` is always reachable.
 * Loading is shown via SCOPED skeletons — the KPI strip and the table
 * content card — not a full-page overlay: switching back from My
 * Notifications remounts the provider (its `hasFetched` resets) and a global
 * spinner would flash over data already cached in Redux. The true-empty
 * message renders compactly inside `GroupsContentCard`; the KPI strip only
 * appears when there ARE groups.
 */
const GroupsTabContent: React.FC<{ tabsBar: React.ReactNode }> = ({
  tabsBar,
}) => {
  const { rows, isGroupsListLoading, hasFetched } = useGroupsTabContext();

  // Busy = (re)fetching or this mount hasn't settled its first fetch yet.
  const isBusy = !hasFetched || isGroupsListLoading;
  // Cached rows survive the tab remount in Redux, so `hasData` is true right
  // away on a tab switch → the KPI strip renders a skeleton while busy. A
  // brand-new tenant (no rows) shows no KPI strip at all.
  const hasData = rows.length > 0;

  return (
    <div className="p-6 space-y-6">
      <GenericCard
        isCollapsible={false}
        variant="primary"
        bodyContent={
          <div className="flex flex-col gap-4">
            {tabsBar}
            {hasData && (
              <div className="border-t border-semantic-border-secondary pt-4">
                <GroupsKpisSection loading={isBusy} />
              </div>
            )}
          </div>
        }
      />

      <GroupsContentCard />
    </div>
  );
};

const NotificationsV2Page: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((s) => s.notificationsV2Account.activeTab);

  const tabsBar = (
    <GenericTabs
      tabs={TAB_OPTIONS as unknown as { label: string; value: string }[]}
      activeTab={activeTab}
      onTabChange={(v) =>
        dispatch(setNotificationsAccountTab(v === "groups" ? "groups" : "me"))
      }
      size="md"
      fullWidth={false}
    />
  );

  if (activeTab === "groups") {
    // Page padding is owned by `GroupsTabContent` so the empty-state
    // branch can render `EmptyItems` flush against the layout and rely
    // on its built-in `min-h-[calc(100vh-10rem)]` centering without
    // overflowing the viewport.
    return (
      <GroupsTabProvider>
        <GroupsTabContent tabsBar={tabsBar} />
      </GroupsTabProvider>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <GenericCard
        isCollapsible={false}
        variant="primary"
        bodyContent={tabsBar}
      />

      <MyNotificationsTab />
    </div>
  );
};

export default NotificationsV2Page;
